import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { documentTypesService } from '../services/api';

/**
 * The app's document types, loaded once from the catalog API.
 *
 * These used to be hardcoded in five separate files plus a Python enum, so
 * adding a type meant a migration and a redeploy. Everything now reads this
 * context, which means a type an admin adds on the Document Types page shows up
 * in every picker, filter and badge without a code change.
 */
const DocumentTypesContext = createContext({
  types: [],
  keys: [],
  labelOf: (key) => key,
  loading: true,
  reload: () => {},
});

const prettify = (key) =>
  String(key ?? '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const DocumentTypesProvider = ({ children }) => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    try {
      setTypes(await documentTypesService.list());
    } catch (err) {
      // A failed load must not blank every filter in the app — callers fall
      // back to prettified keys, which stay readable.
      console.error('Failed to load document types', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const value = useMemo(() => {
    const byKey = new Map(types.map((t) => [t.key, t]));
    return {
      types,
      keys: types.map((t) => t.key),
      labelOf: (key) => byKey.get(key)?.label ?? prettify(key),
      loading,
      reload,
    };
  }, [types, loading, reload]);

  return (
    <DocumentTypesContext.Provider value={value}>{children}</DocumentTypesContext.Provider>
  );
};

export const useDocumentTypes = () => useContext(DocumentTypesContext);
